import {
    GridApiCommon,
    GridCellModes,
    GridRowId,
    GridRowModes,
    GridRowModesModel,
    GridRowSelectionModel,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { create } from "zustand";

import { wait } from "@/helpers/sleep";
import { DepartureProcessItem } from "@/pages/HotelPlanning/departure/utils/process";
import {
    IHotelPlanningDepartureClinicItem,
    IHotelPlanningDepartureResponseItem,
    PatientHotelDepartureStatuses,
    getHotelDepartureRowId,
    isClinicDeparture,
} from "@/schemas/hotelPlanningDepartureSchema";

/**
 * Routing context stored on each change entry so the save dialog can pick the right
 * endpoint (checkpoint-svc vs transport-svc) without holding a full row snapshot.
 */
export type HotelDepartureChangeCtx =
    | { type: "clinic" }
    | { type: "custom"; passengerDbId: string; taskId: string | null };

export type HotelEditableFields = {
    assignedUser?: string | null;
    uniqueCheckpointId: string;
    labelledUser?: string | null;
    /**
     * Driver id labelled before this edit session started (captured once, on first touch) — the
     * unassign-notification recipient, since by save time the row itself already shows the new value.
     */
    previousLabelledUserId?: string | null;
    /** Not an editable cell; excluded from change counts/labels. */
    ctx?: HotelDepartureChangeCtx;
};

/** Keys that represent metadata (not user-editable cells) on a change entry. */
export const HOTEL_DEPARTURE_META_KEYS: (keyof HotelEditableFields)[] = [
    "uniqueCheckpointId",
    "ctx",
    "previousLabelledUserId",
];

export type ShowHotelDepartureProgressDialogType = DepartureProcessItem[] | null;

export type HotelPlanningDepartureFilters = {
    status: PatientHotelDepartureStatuses | null;
    selectedDrivers: string[];
};

type HotelPlanningDepartureTableState = {
    rowModesModel: GridRowModesModel;
    date: DateTime;
    isFullScreen: boolean;
    changes: Record<GridRowId, HotelEditableFields>;
    initialValues: Record<GridRowId, HotelEditableFields>;
    showConfirmationList: GridRowId[];
    selectedRows: GridRowSelectionModel;
    showHotelDepartureProgressDialog: ShowHotelDepartureProgressDialogType;
    filters: HotelPlanningDepartureFilters;
};

/**
 * Istanbul calendar day (yyyy-MM-dd) — the unit checkpoint-svc / transport-svc cut the departure day on.
 */
export const toHotelDepartureIsoDate = (date: DateTime) => date.setZone("Europe/Istanbul").toISODate() ?? "";

/** A day before today (Istanbul) is history: read-only, no assignments / progress actions. */
export const isHotelDeparturePastDate = (date: DateTime) =>
    toHotelDepartureIsoDate(date) < toHotelDepartureIsoDate(DateTime.now());

export const hotelDepartureEditableKeys: (keyof HotelEditableFields)[] = ["assignedUser", "labelledUser"];

type HotelPlanningDepartureTableActions = {
    actions: {
        updateFilters: (filters: Partial<HotelPlanningDepartureFilters>) => void;
        updateSelectedRows: (val?: GridRowSelectionModel) => void;
        handleRowModesModelChange: (rowModesModel: GridRowModesModel) => void;
        handleRowModesModelCancel: (
            id: GridRowId,
            apiRef: GridApiCommon,
            setInitial?: boolean,
            fieldKey?: keyof HotelEditableFields
        ) => void;
        setDate: (date: DateTime) => void;
        updateFullScreen: (val?: boolean) => void;
        getChangedKeys: (
            newRow: IHotelPlanningDepartureResponseItem,
            oldRow: IHotelPlanningDepartureResponseItem
        ) => [HotelEditableFields, number];
        processRowUpdate: (
            newRow: IHotelPlanningDepartureResponseItem,
            oldRow: IHotelPlanningDepartureResponseItem
        ) => IHotelPlanningDepartureResponseItem;
        resetRowChanges: (ids?: GridRowId[]) => void;
        resetRowInitials: (ids?: GridRowId[]) => void;
        handleShowConfirmationList: (val: GridRowId[]) => void;
        updateShowHotelDepartureProgressDialog: (val?: ShowHotelDepartureProgressDialogType) => void;
    };
};

export const useHotelPlanningDepartureStore = create<
    HotelPlanningDepartureTableState & HotelPlanningDepartureTableActions
>()((set, get) => ({
    showConfirmationList: [],
    isFullScreen: false,
    date: DateTime.now(),
    changes: {},
    initialValues: {},
    rowModesModel: {},
    selectedRows: [],
    showHotelDepartureProgressDialog: null,
    filters: {
        status: null,
        selectedDrivers: [],
    },
    actions: {
        updateFilters: (filters) => set({ filters: { ...get().filters, ...filters } }),
        updateShowHotelDepartureProgressDialog: (val) => set({ showHotelDepartureProgressDialog: val ?? null }),
        updateSelectedRows: (val) => set({ selectedRows: val }),
        handleShowConfirmationList: (showConfirmationList) => set({ showConfirmationList }),
        resetRowInitials: (ids?: GridRowId[]) => {
            if (!ids) {
                set({ initialValues: {} });
                return;
            }
            const initialValues = { ...get().initialValues };
            for (const id of ids) {
                delete initialValues[id];
            }
            set({ initialValues });
        },
        resetRowChanges: (ids?: GridRowId[]) => {
            if (!ids) {
                set({ changes: {} });
                return;
            }
            const mockChanges = { ...get().changes };
            for (const id of ids) {
                delete mockChanges[id];
            }

            set({ changes: mockChanges });
        },
        handleRowModesModelCancel: async (id, apiRef, setInitial = true, fieldKey?: string) => {
            try {
                if (!apiRef) return;
                for (const field of hotelDepartureEditableKeys) {
                    if (fieldKey && fieldKey !== field) continue;
                    const initialData = get().initialValues[id];
                    const initialValue = initialData && get().initialValues[id][field as keyof HotelEditableFields];
                    const cell = {
                        id,
                        field,
                    };
                    const cellMode = apiRef.getCellMode(id, field);

                    if (!setInitial || !initialData || !(field in initialData)) {
                        if (cellMode === GridCellModes.Edit) apiRef.stopCellEditMode(cell);
                        continue;
                    }

                    if (cellMode !== GridCellModes.Edit) {
                        apiRef.startCellEditMode(cell);
                    }
                    await wait(25);
                    await apiRef.setEditCellValue({
                        id,
                        field,
                        value: initialValue || null,
                    });
                    await wait(25);

                    apiRef.stopCellEditMode(cell);
                }
            } catch (err) {
                console.error("#handleRowModesModelCancel", err);
            }

            await wait(60);
            get().actions.resetRowChanges([id]);
            get().actions.resetRowInitials([id]);
            get().actions.handleRowModesModelChange({
                ...get().rowModesModel,
                [id]: { mode: GridRowModes.View, ignoreModifications: true },
            });
        },
        handleRowModesModelChange: (rowModesModel) => set({ rowModesModel }),
        updateFullScreen: (isFullScreen) => set({ isFullScreen: isFullScreen ?? !get().isFullScreen }),
        // Pending edits belong to the previously shown day — drop them so they can never be saved
        // against another (possibly historical) day's rows.
        setDate: (date) =>
            set({
                date,
                changes: {},
                initialValues: {},
                rowModesModel: {},
                selectedRows: [],
                showConfirmationList: [],
                showHotelDepartureProgressDialog: null,
            }),
        getChangedKeys: (newRow: IHotelPlanningDepartureResponseItem, oldRow: IHotelPlanningDepartureResponseItem) => {
            const changes: HotelEditableFields = {
                uniqueCheckpointId: isClinicDeparture(newRow) ? newRow.uniqueCheckpointId : "",
            };

            if (newRow.assignedUser !== undefined && newRow.assignedUser !== oldRow.assignedUser) {
                changes.assignedUser =
                    typeof newRow.assignedUser === "string" ? newRow.assignedUser : newRow.assignedUser?.userId || null;
            }

            if (newRow.labelledUser !== undefined && newRow.labelledUser !== oldRow.labelledUser) {
                changes.labelledUser =
                    typeof newRow.labelledUser === "string" ? newRow.labelledUser : newRow.labelledUser?.userId || null;
            }

            return [changes, Object.keys(changes).length];
        },
        processRowUpdate: (
            newRow: IHotelPlanningDepartureResponseItem,
            oldRow: IHotelPlanningDepartureResponseItem
        ) => {
            // History is read-only: last line of defence if an edit path slips past the grid's gates.
            if (isHotelDeparturePastDate(get().date)) return oldRow;

            const rowId = getHotelDepartureRowId(newRow);

            const [changes, changesAmount] = get().actions.getChangedKeys(newRow, oldRow);

            if (!changesAmount) return oldRow;

            const newChanges = { ...get().changes };

            try {
                for (const key of Object.keys(changes)) {
                    const initialData = get().initialValues[rowId];
                    const initialValue = initialData && get().initialValues[rowId][key as keyof HotelEditableFields];
                    const changedVal = changes[key as keyof HotelEditableFields];

                    if (changedVal === initialValue) {
                        delete changes[key as keyof HotelEditableFields];

                        if (newChanges[rowId] && !Object.keys(newChanges[rowId]).length) delete newChanges[rowId];
                    }

                    if (!initialData || !(key in initialData)) {
                        const newVal = { ...get().initialValues };
                        newVal[rowId] = {
                            ...(newVal[rowId] || {}),
                            [key]: (oldRow as IHotelPlanningDepartureClinicItem)[
                                key as keyof IHotelPlanningDepartureClinicItem
                            ] as string & DateTime<boolean>,
                        };

                        set({ initialValues: newVal });

                        if (key === "labelledUser") {
                            const rawOldLabelledUser = oldRow.labelledUser;
                            changes.previousLabelledUserId =
                                typeof rawOldLabelledUser === "string"
                                    ? rawOldLabelledUser
                                    : rawOldLabelledUser?.userId || null;
                        }
                    }
                }
            } catch (err) {
                console.error(err, "err");
            }

            if (newChanges[rowId] || Object.keys(changes).length) {
                const ctx: HotelDepartureChangeCtx = isClinicDeparture(newRow)
                    ? { type: "clinic" }
                    : { type: "custom", passengerDbId: newRow.id ?? "", taskId: newRow.taskId };
                newChanges[rowId] = {
                    ...(newChanges[rowId] || {}),
                    ...changes,
                    ctx,
                };
            }

            set({ changes: newChanges });

            return newRow;
        },
    },
}));

export const useHotelPlanningDepartureRowModesModel = () =>
    useHotelPlanningDepartureStore((state) => state.rowModesModel);

export const useHotelPlanningDepartureDate = () => useHotelPlanningDepartureStore((state) => state.date);
export const useHotelPlanningDepartureIsPastDate = () =>
    useHotelPlanningDepartureStore((state) => isHotelDeparturePastDate(state.date));
export const useHotelPlanningDepartureIsFullScreen = () =>
    useHotelPlanningDepartureStore((state) => state.isFullScreen);
export const useHotelPlanningDepartureShowConfirmationList = () =>
    useHotelPlanningDepartureStore((state) => state.showConfirmationList);

export const useHotelPlanningDepartureChanges = () => useHotelPlanningDepartureStore((state) => state.changes);
export const useHotelPlanningDepartureSelectedRows = () =>
    useHotelPlanningDepartureStore((state) => state.selectedRows);
export const useHotelPlanningDepartureShowHotelDepartureProgressDialog = () =>
    useHotelPlanningDepartureStore((state) => state.showHotelDepartureProgressDialog);
export function useHotelPlanningDepartureFilterField<K extends keyof HotelPlanningDepartureFilters>(
    field: K
): HotelPlanningDepartureFilters[K] {
    return useHotelPlanningDepartureStore((state) => state.filters[field]);
}
export const useHotelPlanningDepartureFilters = () => useHotelPlanningDepartureStore((state) => state.filters);

export const useHotelPlanningDepartureActions = () => useHotelPlanningDepartureStore((state) => state.actions);
